import clsx from 'clsx';
import type { WeaponSkin } from '@vst/shared';
import { SKIN_TIERS } from '@vst/shared';
import { haptic } from '../lib/telegram';

interface Props {
  skin: WeaponSkin;
  price?: number;
  isFavorite?: boolean;
  onToggleFav?: (uuid: string) => void;
  /** Stagger-анимация появления */
  delayMs?: number;
  /** Для Night Market — скидка */
  discountPercent?: number;
  originalPrice?: number;
  compact?: boolean;
}

function tierAccent(tierUuid: string | null): string {
  switch (tierUuid) {
    case SKIN_TIERS.SELECT:    return 'from-tier-select/30 via-transparent to-transparent';
    case SKIN_TIERS.DELUXE:    return 'from-tier-deluxe/30 via-transparent to-transparent';
    case SKIN_TIERS.PREMIUM:   return 'from-tier-premium/30 via-transparent to-transparent';
    case SKIN_TIERS.EXCLUSIVE: return 'from-tier-exclusive/30 via-transparent to-transparent';
    case SKIN_TIERS.ULTRA:     return 'from-tier-ultra/40 via-transparent to-transparent';
    default:                   return 'from-valo-navy-soft via-transparent to-transparent';
  }
}

function tierBorder(tierUuid: string | null): string {
  switch (tierUuid) {
    case SKIN_TIERS.SELECT:    return 'border-tier-select/40';
    case SKIN_TIERS.DELUXE:    return 'border-tier-deluxe/40';
    case SKIN_TIERS.PREMIUM:   return 'border-tier-premium/40';
    case SKIN_TIERS.EXCLUSIVE: return 'border-tier-exclusive/50';
    case SKIN_TIERS.ULTRA:     return 'border-tier-ultra/60';
    default:                   return 'border-valo-navy-border';
  }
}

export function SkinCard({
  skin,
  price,
  isFavorite,
  onToggleFav,
  delayMs = 0,
  discountPercent,
  originalPrice,
  compact = false,
}: Props) {
  const image = skin.displayIcon ?? skin.wallpaper ?? '';

  const handleFav = () => {
    haptic('medium');
    onToggleFav?.(skin.uuid);
  };

  return (
    <div
      className={clsx(
        'group relative clip-corners-sm animate-slide-up',
        'bg-gradient-to-br from-valo-navy-soft via-valo-navy-deep to-valo-navy-deep',
        'border',
        tierBorder(skin.contentTierUuid),
      )}
      style={{ animationDelay: `${delayMs}ms`, animationFillMode: 'backwards' }}
    >
      {/* tier glow */}
      <div
        className={clsx(
          'absolute inset-0 bg-gradient-to-tr opacity-60 pointer-events-none',
          tierAccent(skin.contentTierUuid),
        )}
      />

      {/* Image */}
      <div className={clsx('relative w-full overflow-hidden', compact ? 'aspect-[5/2]' : 'aspect-[16/9]')}>
        {image ? (
          <img
            src={image}
            alt={skin.displayName}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-contain p-2 transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 skeleton" />
        )}

        {/* Favorite heart */}
        {onToggleFav && (
          <button
            onClick={handleFav}
            type="button"
            className={clsx(
              'absolute top-2 right-2 z-10 grid h-9 w-9 place-items-center rounded-full',
              'bg-valo-navy-deep/70 backdrop-blur-sm transition-all',
              'border border-valo-navy-border',
              isFavorite && 'border-valo-red animate-pulse-red',
            )}
            aria-label="toggle favorite"
          >
            <svg
              viewBox="0 0 24 24"
              className={clsx('h-5 w-5 transition-colors', isFavorite ? 'fill-valo-red stroke-valo-red' : 'fill-none stroke-valo-white/80')}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
            </svg>
          </button>
        )}

        {discountPercent && (
          <div className="absolute top-2 left-2 bg-valo-red text-valo-white text-xs font-display px-2 py-1 clip-corners-sm">
            −{discountPercent}%
          </div>
        )}
      </div>

      {/* Meta */}
      <div className="relative px-3 py-2.5 border-t border-valo-navy-border/70 bg-valo-navy-deep/80">
        <p className="font-display text-base text-valo-white truncate leading-tight">
          {skin.displayName}
        </p>
        <div className="flex items-center justify-between mt-1">
          <span className="text-[10px] uppercase tracking-wider text-valo-muted">
            {skin.weaponName ?? '—'}
          </span>
          {price !== undefined && (
            <div className="flex items-baseline gap-1.5">
              {originalPrice && originalPrice !== price && (
                <span className="text-xs text-valo-muted line-through">{originalPrice}</span>
              )}
              <span className="font-mono text-sm font-semibold text-valo-red">
                {price} <span className="text-valo-muted text-[10px]">VP</span>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
